import { confirm, Modal, TooltipProvider } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { UserWithAssignmentDTO } from "@/modules/assign-position/domain/contracts";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { PositionDTO } from "@/modules/positions/domain/contracts";
import { usePositionsQuery } from "@/modules/positions/presentation/api/queries";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useClearAssignment,
  useCreateAssignment,
  useUsersWithAssignmentsQuery,
} from "../api/queries";
import { AssignmentForm } from "../ui/AssignmentForm";
import { AssignmentHistoryModal } from "../ui/AssignmentHistoryModal";
import { EmployeeFilter } from "../ui/EmployeeFilter";
import { EmployeeList } from "../ui/EmployeeList";
import { PositionStructure } from "../ui/PositionStructure";

export function AssignPositionPage() {
  const nav = useNavigate({ from: "/app/assign-position" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/assign-position",
  });

  const users = useUsersWithAssignmentsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  // Get all employees for position structure (no pagination)
  const allEmployees = useUsersWithAssignmentsQuery({
    offset: 0,
    limit: 10000,
  });

  const positions = usePositionsQuery({
    limit: 1000,
    offset: 0,
  });

  const createAssignment = useCreateAssignment();
  const clearAssignment = useClearAssignment();
  const qc = useQueryClient();

  const canManage = useActionPermission([
    "assignments:create",
    "assignments:update",
  ]);
  const assignmentModal = useDisclosure<{
    userId: string;
    userName: string;
    currentPositionId?: string | null;
    currentDepartmentId?: string | null;
  }>();
  const historyModal = useDisclosure<{
    userId: string;
    userName: string;
  }>();

  return (
    <TooltipProvider>
      <DndProvider backend={HTML5Backend}>
        <Header />

        <Main>
          <div className="mb-4">
            <h1 className="font-semibold text-2xl">ກໍາຫນົດຕໍາແໜ່ງ</h1>
            <p className="text-muted-foreground text-sm">
              ເລືອກພະນັກງານແລະກໍາຫນົດຕໍາແໜ່ງ
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Left: Employee List */}
            <div className="flex flex-col rounded-xl border bg-card py-2">
              <div className="px-4 pb-2">
                <h2 className="font-medium text-lg">ພະນັກງານ</h2>
              </div>
              <EmployeeFilter />
              <EmployeeList
                data={users.data?.data ?? []}
                isLoading={users.isLoading}
                offset={search.offset ?? 0}
                limit={search.limit ?? 20}
                totalCount={users.data?.meta?.total ?? 0}
                onPaginationChange={(offset, limit) =>
                  nav({ search: { ...search, offset, limit } })
                }
                onAssign={(user) => {
                  assignmentModal.openWith({
                    userId: user.id,
                    userName: user.name ?? user.email,
                    currentPositionId: user.currentPositionId,
                    currentDepartmentId: user.currentDepartmentId,
                  });
                }}
                onClearPosition={
                  canManage
                    ? async (user) => {
                        const title =
                          user.currentPositionTitle ?? user.currentPositionId;
                        const ok = await confirm({
                          title: "ເອົາຕໍາແໜ່ງອອກ",
                          description: `ເອົາຕໍາແໜ່ງ "${title ?? ""}" ອອກຈາກ ${user.name ?? user.email} ບໍ?`,
                          actionText: "ເອົາອອກ",
                        });
                        if (!ok) return;
                        await clearAssignment.mutateAsync(user.id);
                      }
                    : undefined
                }
                clearingUserId={
                  clearAssignment.isPending
                    ? clearAssignment.variables
                    : undefined
                }
                onViewHistory={(user) => {
                  historyModal.openWith({
                    userId: user.id,
                    userName: user.name ?? user.email,
                  });
                }}
                canManage={!!canManage}
              />
            </div>

            {/* Right: Position Structure */}
            <div className="flex flex-col rounded-xl border bg-card pt-2">
              <div className="px-4 pb-2">
                <h2 className="font-medium text-lg">ໂຄງສ້າງຕໍາແໜ່ງ</h2>
                <p className="text-muted-foreground text-xs">
                  ລາກພະນັກງານຈາກຝັ່ງຊ້າຍມາວາງທີ່ນີ້
                </p>
              </div>
              <PositionStructure
                positions={positions.data?.data ?? []}
                employees={allEmployees.data?.data ?? []}
                isLoading={positions.isLoading || allEmployees.isLoading}
                onDrop={async (userId, positionId) => {
                  const user = allEmployees.data?.data.find(
                    (u: UserWithAssignmentDTO) => u.id === userId,
                  );
                  const position = positions.data?.data.find(
                    (p: PositionDTO) => p.id === positionId,
                  );
                  if (!user || !position) return;

                  const isUpdate = !!user.currentPositionId;
                  const isCrossDepartment =
                    user.currentDepartmentId &&
                    position.departmentId !== user.currentDepartmentId;

                  let description = isUpdate
                    ? `ປ່ຽນຕໍາແໜ່ງ ${user.name ?? user.email} ເປັນ "${position.title}" ບໍ?`
                    : `ກໍາຫນົດ ${user.name ?? user.email} ເປັນ "${position.title}" ບໍ?`;

                  if (isCrossDepartment) {
                    description +=
                      "\n\n⚠️ ການຍ້າຍຂ້າມພະແນກອາດຈະກະທົບຕໍ່ສິດທິ / workflow";
                  }

                  const ok = await confirm({
                    title: isUpdate ? "ປ່ຽນຕໍາແໜ່ງ" : "ກໍາຫນົດຕໍາແໜ່ງ",
                    description,
                    actionText: isUpdate ? "ປ່ຽນ" : "ກໍາຫນົດ",
                  });

                  if (!ok) return;

                  // Use assignmentsApi directly for update to avoid hook call in callback
                  if (isUpdate) {
                    const { assignmentsApi } = await import("../api/client");
                    await assignmentsApi.update(userId, {
                      positionId,
                      notes: null,
                    });
                    // Invalidate queries manually
                    qc.invalidateQueries({ queryKey: ["assignments"] });
                    qc.invalidateQueries({ queryKey: ["positions"] });
                  } else {
                    await createAssignment.mutateAsync({
                      userId,
                      positionId,
                      isPrimary: true,
                      notes: null,
                    });
                  }
                }}
                canManage={!!canManage}
              />
            </div>
          </div>

          {/* Assignment Modal */}
          <Modal
            open={!!assignmentModal.isOpen}
            onOpenChange={assignmentModal.toggle}
            title={`ກໍາຫນົດຕໍາແໜ່ງ: ${assignmentModal.data?.userName ?? ""}`}
            size="sm"
          >
            <div className="min-w-0 max-w-full overflow-hidden">
              <AssignmentForm
                userId={assignmentModal.data?.userId ?? ""}
                currentPositionId={assignmentModal.data?.currentPositionId}
                currentDepartmentId={assignmentModal.data?.currentDepartmentId}
                onSubmit={async () => {
                  assignmentModal.close();
                }}
              />
            </div>
          </Modal>

          {/* History Modal */}
          <AssignmentHistoryModal
            open={!!historyModal.isOpen}
            onOpenChange={historyModal.toggle}
            userId={historyModal.data?.userId ?? ""}
            userName={historyModal.data?.userName ?? ""}
          />
        </Main>
      </DndProvider>
    </TooltipProvider>
  );
}
